<template>
  <div class="container">
    <div class="header">
      <info-tip
          :tip="shortcutKeyCodeLabel"
          :show-icon="false"
          class="pull-right"
      >
        <button  class="btn btn-carma btn-square pull-right" @click="addRow">
          <i class="fas fa-plus"></i> {{ $t('autoAssign.form.create.add') }}
        </button>
      </info-tip>


      <div v-if="selectedRows.length > 0" class="datatable-bulk-actions">
        <span>{{selectedLabel}}</span>
        <div class="actions">
          <div class="action btn-primary" @click="showPanel = 'bulkDelete'">Delete</div>
        </div>
      </div>

      <div class="card clearfix">
        <datatable ref="table" :data="table" :loading="tableLoading"/>
        <div class="loading-next-page" v-if="loadingNextPage"><loader /></div>
      </div>
    </div>

    <side-panel v-if="showPanel" :onClose="closePanel">
      <template v-if="showPanel === 'removeRule'">
        <load-content :loading="isRulesBusy">
          <confirm-delete
              :label="ruleModel.name"
              :onDelete="onDeleteRule"
          />
        </load-content>
      </template>

      <template v-if="showPanel === 'bulkDelete'">
        <load-content :loading="isRuleBusy">
          <confirm-delete
              :label="selectedLabel"
              :bulk="true"
              :onDelete="() => onSubmitRulesToDelete(selectedRows)"
          />
        </load-content>
      </template>

      <template v-if="showPanel === 'addEditRule'">
        <form-renderer
          :loading="isRuleBusy"
          :id="'create-rule-form'"
          :title="`autoAssign.form.${ruleModel ? 'edit' : 'create'}.title`"
          submitButtonLabel="autoAssign.form.save"
          :model="ruleModel"
          :fields="fields"
          :fieldsAdditions="addFieldsAdditions"
          @updateModel="onUpdateModel"
          @onSave="onAddEditRule"
        />
      </template>
    </side-panel>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import moment from 'moment';
  import _ from 'lodash';

  import LoadContent from '../load-content';
  import Loader from "../loader";
  import Datatable from '../datatable';
  import SidePanel from '../side-panel';
  import ConfirmDelete from "../confirm-delete";
  import FormRenderer from '../form-renderer';
  import CountriesList from 'countries-list';
  import { FIELD_TYPE, VALIDATIONS } from '../../config/const';
  import { propsForMixin } from "../../helpers/application";
  import runShortcut from "../../mixins/shortcuts"
  import InfoTip from "../info-tip";


  export default {
    name: 'ArticleGroup',
    components: {
      datatable: Datatable,
      LoadContent,
      SidePanel,
      ConfirmDelete,
      FormRenderer,
      Loader,
      InfoTip
    },
    computed: {
      ...mapState({
        articleAssignmentRules: state => !_.isEmpty(_.get(state.articleAssignmentRules.articleAssignmentRules, 'data.data.data')) ?  state.articleAssignmentRules.articleAssignmentRules.data.data.data : [],
        articleAssignmentRulesPagination: state => !_.isEmpty(_.get(state.articleAssignmentRules.articleAssignmentRules, 'data.data.pagination')) ? state.articleAssignmentRules.articleAssignmentRules.data.data.pagination : {},
        articleAssignmentTags: state => state.articleAssignmentRules.articleAssignmentTags.data ? state.articleAssignmentRules.articleAssignmentTags.data : [],
        articleAssignmentPrivateTags: state => state.articleAssignmentRules.articleAssignmentPrivateTags.data ? state.articleAssignmentRules.articleAssignmentPrivateTags.data : [],
        articleStatus: state => state.articles.article_status.data ? state.articles.article_status.data : [],
        isRulesBusy: state => state.articleAssignmentRules.articleAssignmentRules.isBusy,
        isRuleBusy: state => state.articleAssignmentRules.articleAssignmentRule.isBusy,
        user: state => state.user.user.data,
        userCoder: state => state.user.userCoder.data ? state.user.userCoder.data : [],
        groups:  state => state.articleAssignmentRules.groups.data,
        articleAssignmentOutlets: state => state.articleAssignmentRules.articleAssignmentOutlets.data ? state.articleAssignmentRules.articleAssignmentOutlets.data : [],
        isOutletsBusy: state => state.articleAssignmentRules.articleAssignmentOutlets.isBusy,
        relevancy: state => state.articleAssignmentRules.articleAssignmentRule.relevancies?.data ? state.articleAssignmentRules.articleAssignmentRule.relevancies.data : [],
        isOutletGroupsBusy: state => state.outletGroups.outlet_groups.isBusy,
        outletGroups: state => state.outletGroups.outlet_groups.data,
        articleAssignmentAiModels: state => state.articleAssignmentRules?.articleAssignmentAiModels?.data?.data || [],
        project: state => state.projects?.project?.data || {},
        socialPostTypes: state => state.articleAssignmentRules.socialPostTypes?.data?.data || [],
        relevancyList: state => state.relevancy.relevancy?.data?.dataList || [],
      }),
      ...mapState('mediaTypes', [
        'mediaTypes'
      ]),
      ...mapState('editorCountries', [
        'editorCountries'
      ]),

      groupOptions() {

        if (!this.groups || !this.groups.length)
          return [];

        return this.groups.map(item => {
          return {
            value: item.id,
            label: item.name
          }
        })
      },

      articlesStatusOptions() {
        return this.articleStatus.map(item => {
          return {
            value: item.id,
            label: item.status
          }
        })
      },

      selectedLabel() {
        return `${this.selectedRows.length} ${this.selectedRows.length == 1 ? 'rule' : 'rules'} selected`
      },

      mediaTypesWithLabels () {

        return this.mediaTypes.data && this.mediaTypes.data.map( x => ({
          label: this.$i18n.t(`outlets.media_types.${x.mediatype.replace("-","_")}`),
          value: x.mediatype
        })) || [] ;
      },

      relevanciesOptions() {
       const opt = [
        {label: this.$i18n.t('relevancy.form.auto_assign.critical_limit'), value: 'critical'},
        {label: this.$i18n.t('relevancy.form.auto_assign.high_limit'), value: 'high'},
        {label: this.$i18n.t('relevancy.form.auto_assign.medium_limit'), value: 'medium'},
        {label: this.$i18n.t('relevancy.form.auto_assign.low_limit'), value: 'low'}
      ];
      return opt;
      },

      getAiModelOptions () {
        return this.articleAssignmentAiModels?.map(m => ({ label: m?.prompt_key + ' ' + m?.name , value: m?.id }));
      },

      fields () {
        const socialFields = [];
        if (this.showSocialFilters) {
          socialFields.push('social_media_platforms', 'account_type', 'post_types', 'exclude_post_types', 'minimum_followers', 'include_handle', 'exclude_handle', 'user_locations');
        }
        const projectBaseFields = [];
        if(this.project.enable_auto_coder){
          projectBaseFields.push('ai_models');
        }
        
        return [
          'name',
          'tags',
          'exclude_tags',
          'private_tags',
          'exclude_private_tags',
          'country',
          'media_type',
          'language',
          'tiers',
          'relevancy',
          'relevancy_scope',
          'relevancy_setting_id',
          'outlet_groups',
          'coder_id',
          'state_id',
          'priority',
          'groups',
          'keywords',
          'outlets_multiple',
          'filter_label',
          'action_label',
          ...socialFields,
          ...projectBaseFields
        ]
      },

      getProjectBaseFields (){
        let extraFields = {};
       if(this.project.enable_auto_coder){
         extraFields.ai_models = {
            style: 'flex-basis: 50%; order: 30',
            customClass: 'pr10',
            options: this.getAiModelOptions,
          }
        }
        return extraFields;
      },

      relevancyScope() {
        const options = Array.from(
          new Set(this.relevancyList.map(ele => ele.rs_type))
        ).map(rs_type => {
          return { label: rs_type, value: rs_type };
        });
        return options;
      },

      addFieldsAdditions () {
        return {
          name: {translate_label: 'autoAssign.labels.name.title', style: 'flex-basis: 70%; order: 1', customClass: 'pr10'},
          priority: { style: 'flex-basis: 20%; order: 2'},
          filter_label: { 
            style: 'flex-basis: 100%; border-bottom: 1px solid #ddd; order: 3',
            customLabelClass: 'text-orange', 
          },
          tags: {
            style: 'flex-basis: 50%; order: 4',
            options: this.tags,
            preselectFirst: false,
            multiple: true,
            searchable: true,
            customClass: 'pr10',
          },
          exclude_tags: {
            style: 'flex-basis: 50%; order: 5',
            label: this.$tc('tag.labels.exclude_tags.title', 2),
            translatePlaceholder: this.$tc('tag.labels.exclude_tags.select', 2),
            options: this.excludeTags,
            preselectFirst: false,
            multiple: true,
            searchable: true,
          },
          private_tags: {
            style: 'flex-basis: 50%; order: 6',
            options: this.privateTags,
            preselectFirst: false,
            multiple: true,
            searchable: true,
            customClass: 'pr10',
          },
          exclude_private_tags: {
            style: 'flex-basis: 50%; order: 7',
            label: this.$tc('tag.labels.exclude_private_tags.title', 2),
            translatePlaceholder: this.$tc('tag.labels.exclude_private_tags.select', 2),
            options: this.excludePrivateTags,
            preselectFirst: false,
            multiple: true,
            searchable: true,
          },
          media_type: {
            style: 'flex-basis: 50%; order: 8',
            customClass: 'pr10',
            options: this.mediaTypesWithLabels,
            translatePlaceholder: 'outlets.media_type.multi_select',
            multiple: true
          },
          relevancy_scope: {
            style: 'flex-basis: 50%; order: 9',
            label: this.$tc('carma_ai.filtration.relevancy_scope'),
            name: 'relevancy_scope',
            options: this.relevancyScope || [],
            multiple: false,
            validate: null
          },
          relevancy_setting_id: {
            style: 'flex-basis: 50%; order: 10',
            label: this.$tc('carma_ai.filtration.relevancy_name'),
            name: 'relevancy_setting_id',
            options: this.slectedRelevancyName || [],
            customClass: 'pr10',
            multiple: false,
            validate: null
          },
          relevancy: {
            style: 'flex-basis: 50%; order: 11',
            options: this.relevanciesOptions || [],
            multiple: true,
            validate: null,
          },
          country: {
            style: 'flex-basis: 50%; order: 12',
            customClass: 'pr10',
            options: this.editorCountries && this.editorCountries.data || [],
            translatePlaceholder: 'outlets.country.multi_select',
            multiple: true
          },
          tiers: { style: 'flex-basis: 50%; order: 13', customClass: 'pr10',},
          language: {
            style: 'flex-basis: 50%; order: 14',
            customClass: 'pr10',
            options: Object.keys(CountriesList.languages).map(key => {
              return {
                value: key,
                label: CountriesList.languages[key].name
              }
            }),
            searchable: true,
            multiple: true,
          },
          outlet_groups: {
            style: 'flex-basis: 50%; order: 15',
            options: this.outletGroupsOptions || [],
            multiple: true,
            searchable: true,
            hideSelected: true,
            loading: this.isOutletGroupsBusy,
            showNoResults: true,
            internalSearch: false,
            onSearchChange: (value) => this.fetchOutletGroups(value),
            update: (value) => this.updateSelectedOutletGroups(value),
          },
          outlets_multiple: {
            style: 'flex-basis: 100%; order: 16',
            customClass: '',
            options: this.outletOptions || [],
            customLabel: this.outletOptionLabel,
            translatePlaceholder: 'outlets.multi_select',
            multiple: true,
            searchable: true,
            hideSelected: true,
            loading: this.isOutletsBusy,
            showNoResults: true,
            internalSearch: false,
            onSearchChange: (value) => this.fetchOutlets(value),
            update: (value) => this.updateSelectedOutlets(value),
          },
          social_media_platforms: {
            style: 'flex-basis: 50%; order: 17',
            options: this.socialMediaPlatformOptions,
            customClass: 'pr10',
            multiple: true,
            searchable: true,
            hideSelected: true,
          },
          account_type: {
            style: 'flex-basis: 50%; order: 18',
            options: this.accountTypeOptions,
            multiple: false,
            hideSelected: true,
          },
          post_types: {
            style: 'flex-basis: 50%; order: 19',
            options: this.postTypes,
            customClass: 'pr10',
            multiple: true,
            searchable: true,
            hideSelected: true,
            showNoResults: false,
            internalSearch: true,
          },
          exclude_post_types: {
            style: 'flex-basis: 50%; order: 20',
            options: this.excludePostTypes,
            multiple: true,
            searchable: true,
            hideSelected: true,
            showNoResults: false,
            internalSearch: true,
          },
          include_handle: {
            style: 'flex-basis: 50%; order: 21',
            customClass: 'pr10',
            options: this.includeHandleOptions,
            taggable: true
          },
          exclude_handle: {
            style: 'flex-basis: 50%; order: 22',
            options: this.excludeHandleOptions,
            taggable: true,
          },
          minimum_followers: {
            style: 'flex-basis: 50%; order: 23',
            customClass: 'pr10',
          },
          user_locations: {
            style: 'flex-basis: 50%; order: 24',
            options: this.editorCountries && this.editorCountries.data || [],
            multiple: true,
            searchable: true
          },
          keywords: {
            type: FIELD_TYPE.TEXTAREA,
            style: 'flex-basis: 50%; order: 25',
            translate_label: 'articles.keywords',
            validate: [VALIDATIONS.SEARCH_STRING, VALIDATIONS.BRACKETS_MATCH]
          },
          action_label: { 
            style: 'flex-basis: 100%; border-bottom: 1px solid #ddd; order: 26',
            customLabelClass: 'text-orange', 
          },
          coder_id: {
            translate_label: 'coder.labels.name.coder',
            style: 'flex-basis: 50%; order: 27',
            customClass: 'pr10',
            options: this.coderOptions || [],
            preselectFirst: false,
            multiple: false,
            searchable: true,
            validate: null,
          },
          groups: {
            style: 'flex-basis: 50%; order: 28',
            options: this.groupOptions,
            label: () => this.$tc('group.labels.title', 1)
          },
          state_id: {
            translate_label: 'articles.status',
            style: 'flex-basis: 50%; order: 29',
            customClass: 'pr10',
            options: this.articlesStatusOptions || [],
            preselectFirst: false,
            multiple: false,
            searchable: true
          },
          ...this.getProjectBaseFields,
        }
      },

      coderOptions() {
        return this.userCoder.map(t => ({ value: t.id, label: (t.first_name + ' ' + t.last_name) }));
      },

      projectId () {
        return this.$route.params.project_uid;
      },

      tableLoading(){
        return (this.isRulesBusy === true || this.isRuleBusy === true) && this.pagination.page === 1;
      },

      loadingNextPage() {
        // return true;
        return this.isRulesBusy && this.pagination.page > 1;
      },

      table () {
        const { pagination, articleAssignmentRules, projectId } = this;
        const self = this;
        return {
          body: articleAssignmentRules,
          columns: [
            {
              key: 'name',
              label: self.$i18n.t('admin.headers.name'),
              orderable: true,
              render: (row) => { return (<a class="link-pointer" ref={`row${row.id}`} onClick={() => this.onRuleSelect(row.id)}>{row.name}</a>); }
            },
            {
              key: 'priority',
              label: self.$i18n.t('autoAssign.labels.priority.title'),
              orderable: true
            },
            {
              key: 'created_at',
              label: self.$i18n.t('admin.headers.date'),
              orderable: true,
              render: (row) => {
                return row.created_at ? <span class="date">{moment(row.created_at).format('D MMM YYYY')}</span> : ''
              }
            },
            {
              key: 'last_updated',
              label: self.$i18n.t('admin.headers.last_updated'),
              orderable: true,
              render: (row) => {
                return row.updated_at ? <span class="date">{moment(row.updated_at).format('D MMM YYYY')}</span> : ''
              }
            },
          ],
          order: {
            column: pagination.order,
            direction: pagination.sort,
            onChange: (column, direction) => {
              this.onSortTable(column, direction);
            }
          },
          addRow: {
            enabled: this.user.code_admin && articleAssignmentRules.length > 5,
            label: self.$i18n.t('admin.add_query'),
            onClick: () => self.onAddRule()
          },
          removeRow: {
            enabled: this.user.code_admin,
            onClick: (row) => {
              self.showPanel = 'removeRule';
              return {
                ...self.ruleModel = row
              }
            }
          },
          bulkActions: {
            enabled: true,
            onSelect: function (rows) {
              self.selectedRows = rows.map(row => row.id);
            }.bind(this)
          }
        }
      },

      outletOptions () {
        const availableOutlets = [...this.articleAssignmentOutlets.filter(o => !this.selectedOutlets.find(o2 => o2.id == o.id)), ...this.selectedOutlets];
        const options = availableOutlets;
        return options;
      },

      outletGroupsOptions () {
        const availableOGOptions = [...this.outletGroups.filter(og => !this.selectedOutletGroups.find(sog => sog.value == og.value)), ...this.selectedOutletGroups];
        const options = availableOGOptions;
        return options;
      },
      socialMediaPlatformOptions () {
        return [
          {label: this.$t('articles.social_posts.providers.fb'), value: 'facebook'},
          {label: this.$t('articles.social_posts.providers.ig'), value: 'instagram'},
          {label: this.$t('articles.social_posts.providers.yt'), value: 'youtube'},
          {label: this.$t('articles.social_posts.providers.gnip'), value: 'gnip'},
          {label: this.$t('articles.social_posts.providers.reddit_mention'), value: 'reddit'},
          {label: this.$t('articles.social_posts.providers.tiktok'), value: 'tiktok'},
          {label: this.$t('articles.social_posts.providers.social-linkedin'), value: 'linkedin'}
        ];
      },
      accountTypeOptions () {
        return [
          {label: this.$t('articles.social_posts.account_types.verified'), value: 'true'},
          {label: this.$t('articles.social_posts.account_types.unverified'), value: 'false'},
        ]
      },

      socialPostTypeOptions () {
        return this.socialPostTypes.map(dt => {
          return { value: `${dt.provider}.${dt.type}`, label: `${this.$t('articles.social_posts.providers.' + dt.provider)} ${this.$t('articles.social_posts.types.' + dt.type)}` }
        })
      }
    },

    mounted () {
      this.fetchRules();
      this.fetchOptions();
      this.getArticleStatus(this.projectId);
      this.getGroups(this.projectId);

      this.$nextTick(() => {
        this.scroll();
      });
      window.addEventListener('scroll', this.handleScroll);
    },
    beforeDestroy(){
      window.removeEventListener('scroll', this.handleScroll);
    },
    watch: {
      pagination: {
        handler(){
          const { pagination, projectId } = this;
          this.getAssignmentRules({ projectId, pagination });
        },
        deep: true
      },
      'ruleModel': {
        handler(rule){
          if(rule){
            this.availableTags(rule.exclude_tags);
            this.availableExcludedTags(rule.tags);
            this.availablePrivateTags(rule.exclude_private_tags);
            this.availableExcludedPrivateTags(rule.private_tags);
            this.availablePostTypes(rule.exclude_post_types);
            this.availableExcludePostTypes(rule.post_types);
          }   
        },
        deep: true
      },
      'socialPostTypes':{
        handler(value){
          if(value){
            this.availablePostTypes();
            this.availableExcludePostTypes();
          }
        },
      },
      project: {
        handler(val){
          const { projectId } = this;
          if(val.enable_auto_coder){
            this.getAssignmentAIModels({ projectId });
          }
        }
      },
      'articleAssignmentTags.data':{
        handler(value){
          if(value){
            this.availableTags();
            this.availableExcludedTags();
          }   
        }
      },
      'articleAssignmentPrivateTags.data':{
        handler(value){
          if(value){
            this.availablePrivateTags();
            this.availableExcludedPrivateTags();
          }   
        }
      }
    },
    methods: {
      ...mapActions({
        getProject: "projects/getProject",
        getUserCoder: 'user/getUserCoder',
        getMediaTypes: 'mediaTypes/getMediaTypes',
        getCountries: 'editorCountries/getCountries',
        getArticleStatus: 'articles/getArticleStatus',
        getAssignmentRules: 'articleAssignmentRules/getAssignmentRules',
        getAssignmentRule: 'articleAssignmentRules/getAssignmentRule',
        getGroups: 'articleAssignmentRules/getGroups',
        createAssignmentRule: 'articleAssignmentRules/createAssignmentRule',
        updateAssignmentRule: 'articleAssignmentRules/updateAssignmentRule',
        removeAssignmentRules: 'articleAssignmentRules/removeAssignmentRules',
        getAssignmentTags: 'articleAssignmentRules/getAssignmentTags',
        getAssignmentPrivateTags: 'articleAssignmentRules/getAssignmentPrivateTags',
        getAssignmentOutlets: 'articleAssignmentRules/getAssignmentOutlets',
        getRelevancies: 'articleAssignmentRules/getAssignmentRule/getRelevancies',
        getOutletGroups: 'outletGroups/getOutletGroups',
        getAssignmentAIModels: 'articleAssignmentRules/getAssignmentAIModels',
        getAssignmentSocialPostTypes: 'articleAssignmentRules/getAssignmentSocialPostTypes',
        getRelevancy : 'relevancy/getRelevancy'
      }),

      onUpdateModel (model, field) {
        switch (field.name) {
          case 'relevancy_scope':
            this.getRelevancyNameOptions(model.relevancy_scope);
            model.relevancy_setting_id = null;
            break;
          case 'tags':
            this.availableExcludedTags(model.tags);
            break;
          case 'exclude_tags':
            this.availableTags(model.exclude_tags);
            break;
          case 'private_tags':
            this.availableExcludedPrivateTags(model.private_tags);
            break;
          case 'exclude_private_tags':
            this.availablePrivateTags(model.exclude_private_tags);
            break;
          case 'media_type':
          this.showSocialFilters = model.media_type.indexOf('social-networks') > -1;
            break;
          case 'post_types':
            this.availableExcludePostTypes(model.post_types);
            break;
          case 'exclude_post_types':
            this.availablePostTypes(model.exclude_post_types);
            break;
          case 'include_handle':
            this.socialIncludeHandleOptions(model.include_handle);
            break;
          case 'exclude_handle':
            this.socialExcludeHandleOptions(model.exclude_handle);
            break;
          case 'coder_id':
            if(model.coder_id === 891){
              // Auto coder (891) set the default status to Assigned (3)
              model.state_id = 3;
            }
            break;
          }
      },

      fetchRules(){
        const { pagination, projectId } = this;
        this.getProject({ projectUid: projectId });
        this.getAssignmentRules({ projectId, pagination });
        this.getRelevancy({projectId: projectId });
        this.getAssignmentTags({ projectId });
        this.getAssignmentPrivateTags({ projectId });
        this.getUserCoder({ projectId });
        this.getMediaTypes({ projectId });
        this.getCountries({ projectId });
        this.getAssignmentOutlets({ projectId, pagination: { page: 1, perPage: 20 } });
        this.getOutletGroups({projectId, query: ''});
        this.getAssignmentSocialPostTypes({ projectId });
      },

      fetchOptions(){
        const { pagination, projectId } = this;
        this.getAssignmentRules({ projectId, pagination });
      },

      fetchOutlets(value){
        this.getAssignmentOutlets({ projectId: this.projectId, pagination: { page: 1, perPage: 20 }, filterValues: { name: value } });
      },

      fetchOutletGroups(value){
        this.getOutletGroups({projectId: this.projectId, query: value});
      },

      outletOptionLabel ({ id, name, media_subtype, country_code }) {
        const country = CountriesList.countries[country_code.toUpperCase()];
        const mediaType = this.$t(`outlets.media_types.${media_subtype}`);
        return `${name} (ID:${id} - ${mediaType} - ${country.name})`;
      },

      updateSelectedOutlets(model) {
        const options = this.outletOptions.filter(outlet => model.outlets.indexOf(outlet.id) > -1);
        this.selectedOutlets = options;
      },

      updateSelectedOutletGroups(model) {
        const options = this.outletGroupsOptions.filter(og => model.outlet_groups.indexOf(og.value) > -1);
        this.selectedOutletGroups = options;
      },

      tagOptions() {
        return this.articleAssignmentTags.data.map(tag => {
          return {
            value: tag,
            label: tag.category + ' - ' + tag.name
          }
        });
      },

      filterTags(tagsToExclude) {
        return this.tagOptions()?.filter(option => {
          return !tagsToExclude.some(item => _.isEqual(item, option.value));
        });
      },

      availableTags(value) {
        this.tags = this.filterTags(value || []);
      },

      availableExcludedTags(value) {
        this.excludeTags = this.filterTags(value || []);
      },

      privateTagOptions() {
        return this.articleAssignmentPrivateTags.data.map(private_tag => {
          return {
            value: private_tag,
            label: private_tag.category + ' - ' + private_tag.name
          }
        });
      },

      filterPrivateTags(tagsToExclude) {
        return this.privateTagOptions()?.filter(option => {
          return !tagsToExclude.some(item => _.isEqual(item, option.value));
        });
      },

      availablePrivateTags(value) {
        this.privateTags = this.filterPrivateTags(value || []);
      },

      availableExcludedPrivateTags(value) {
        this.excludePrivateTags = this.filterPrivateTags(value || []);
      },

      socialPlatformOptions() {
        return this.socialPostTypes.map(dt => {
          return { value: `${dt.provider}.${dt.type}`, label: `${this.$t('articles.social_posts.providers.' + dt.provider)} ${this.$t('articles.social_posts.types.' + dt.type)}` }
        })
      },

      filteredOptions(optionsToExclude) {
        return this.socialPlatformOptions()?.filter(option => {
          return !optionsToExclude.some(item => _.isEqual(item, option.value));
        });
      },
      availablePostTypes(value) {
        this.postTypes = this.filteredOptions(value || []);
      },
      availableExcludePostTypes(value) {
        this.excludePostTypes = this.filteredOptions(value || []);
      },

      fixTagsPlacement(rule, type = 'tags'){
        if(type === 'tags'){
          const filteredTags = (rule.tags || []).filter(tag =>
            this.articleAssignmentTags.data.some(dataTag =>
              dataTag.category === tag.category && dataTag.name === tag.name
            )
          );
          return filteredTags
        }

        if(type === 'private_tags'){
          const filteredPrivateTags = [
            ...(rule.private_tags || []),
            ...(rule.tags || []).filter(tag =>
              this.articleAssignmentPrivateTags.data.some(dataTag =>dataTag.category === tag.category && dataTag.name === tag.name) &&
              !this.articleAssignmentTags.data.some(publicTag => publicTag.category === tag.category && publicTag.name === tag.name)
            )
          ];
          return filteredPrivateTags
        }

        return rule.tags
      },

      onRuleSelect(ruleId) {
        this.showPanel = 'addEditRule';
        const rule = this.articleAssignmentRules.find((rule) => rule.id == ruleId);
        const selected = this.relevancyList.find((ele) => ele.id === rule.relevancy_setting_id);
        const rsType = selected?.rs_type || null;
        this.getRelevancyNameOptions(rsType);
        this.ruleModel = {
          ...rule,
          tags: this.fixTagsPlacement(rule, 'tags') || [],
          exclude_tags: rule.exclude_tags || [],
          private_tags: this.fixTagsPlacement(rule, 'private_tags') || [],
          exclude_private_tags: rule.exclude_private_tags || [],
          groups: rule.groups || [],
          media_type: rule.media_types || [],
          country: rule.countries || [],
          coder_id: rule.coder_id,
          state_id: rule.state_id,
          language: rule.languages || [],
          outlets: rule.outlets && rule.outlets.map(outlet => outlet.id) || [],
          keywords: rule.keywords,
          relevancy: rule.relevancies || [],
          relevancy_scope: rsType,
          relevancy_setting_id: rule.relevancy_setting_id || null,
          outlet_groups: rule.outlet_groups && rule.outlet_groups.map(og => og.value) || [],
          ai_models: rule.ai_models || []
        }

        this.showSocialFilters = this.ruleModel.media_type.indexOf('social-networks') > -1;

        if (this.showSocialFilters) {
          this.socialIncludeHandleOptions(rule.include_handle);
          this.socialExcludeHandleOptions(rule.exclude_handle);
          this.ruleModel.social_media_platforms = rule.social_media_platforms || [];
          this.ruleModel.account_type = '' + rule.account_type;
          this.ruleModel.post_types = rule.post_types || [];
          this.ruleModel.exclude_post_types = rule.exclude_post_types || [];
          this.ruleModel.minimum_followers = rule.minimum_followers;
          this.ruleModel.include_handle = rule.include_handle;
          this.ruleModel.exclude_handle = rule.exclude_handle;
          this.ruleModel.user_locations = rule.user_locations;
        }

        this.selectedOutlets = rule.outlets;
        this.selectedOutletGroups = rule.outlet_groups;
      },

      addRow() {
        this.showPanel = 'addEditRule';
        this.selectedOutlets = [];
        this.selectedOutletGroups = [];
        this.showSocialFilters = false;
      },

      closePanel: function() {
        this.showPanel = false;
        this.ruleModel = null;
      },

      onSortTable: function(column, direction) {
        this.pagination.order = column;
        this.pagination.sort = direction;
        this.pagination.page = 1;
        
        const { pagination, projectId } = this;
        this.getAssignmentRules({ projectId, pagination })
      },

      onDeleteRule () {
        const { projectId, ruleModel } = this;
        if(!this.isRuleBusy) {
          this.removeAssignmentRules({
            projectId,
            ruleIds: ruleModel.id
          });

          this.closePanel();
        }
      },

      socialIncludeHandleOptions(includeHandle) {
        if (includeHandle && Array.isArray(includeHandle)){
          this.includeHandleOptions = includeHandle.map(handle => ({
          value: handle.value || handle,
          label: handle.label || handle
          }));
        }
      },
      socialExcludeHandleOptions(excludeHandle) {
        if (excludeHandle && Array.isArray(excludeHandle)){ 
          this.excludeHandleOptions = excludeHandle.map(handle => ({
            value: handle.value || handle,
            label: handle.label || handle
          }));
        }
      },

      onAddEditRule (rule) {
        const { projectId } = this;

        if (rule.relevancy_scope && rule.relevancy_setting_id && _.isEmpty(rule.relevancy)) {
          this.$notify({
            group: 'store_notifications',
            type: 'error',
            text: this.$t('relevancy.form.auto_assign.relevancy_required'),
          });
          return;
        }

        const ruleBody = {
          name: rule.name,
          priority: rule.priority,
          tags: (!_.isArray(rule.tags) && !_.isEmpty(_.get(rule, 'tags'))) ? [rule.tags] : rule.tags || [],
          exclude_tags: (!_.isArray(rule.exclude_tags) && !_.isEmpty(_.get(rule, 'exclude_tags')))
            ? [rule.exclude_tags]
            : rule.exclude_tags || [],
          private_tags: (!_.isArray(rule.private_tags) && !_.isEmpty(_.get(rule, 'private_tags')))
            ? [rule.private_tags]
            : rule.private_tags || [],
          exclude_private_tags: (!_.isArray(rule.exclude_private_tags) && !_.isEmpty(_.get(rule, 'exclude_private_tags')))
            ? [rule.exclude_private_tags]
            : rule.exclude_private_tags || [],
          media_types: _.isString(rule.media_type) ? [rule.media_type] : rule.media_type || [],
          tiers: _.isString(rule.tiers) ? [rule.tiers] : rule.tiers || [],
          countries: _.isString(rule.country) ? [rule.country] : rule.country || [],
          languages: _.isString(rule.language) ? [rule.language] : rule.language || [],
          coder_id: rule.coder_id,
          state_id: rule.state_id,
          outlets: _.isString(rule.outlets) ? [rule.outlets] : rule.outlets || [],
          groups: rule.groups,
          keywords: rule.keywords,
          relevancies: rule.relevancy || [],
          relevancy_scope: rule.relevancy_scope || null,
          relevancy_setting_id: rule.relevancy_setting_id || null,
          outlet_groups: rule.outlet_groups || [],
          ai_models: rule.ai_models || []
        }; 

        if (this.showSocialFilters) {
          ruleBody.social_media_platforms = rule.social_media_platforms || [];
          ruleBody.account_type = rule.account_type;
          ruleBody.post_types = rule.post_types || [];
          ruleBody.exclude_post_types = rule.exclude_post_types || [];
          ruleBody.minimum_followers = rule.minimum_followers;
          ruleBody.include_handle = rule.include_handle;
          ruleBody.exclude_handle = rule.exclude_handle;
          ruleBody.user_locations = rule.user_locations
        }

        if(rule.id) {
          ruleBody.id = rule.id;
          this.updateAssignmentRule({ projectId, assignmentRule: ruleBody })
        }
        else {
          this.createAssignmentRule({ projectId, assignmentRule: ruleBody });
        }
        this.closePanel()
      },

      handleScroll(){
        this.scroll();
      },

      resetPagination() {
        const { pagination } = this;
        this.pagination = {
          ...pagination,
          order: 'date',
          sort: 'desc',
          page: 1
        }
      },

      scroll: function() {
        const { projectId, queries, isRulesBusy, queriesPagination } = this;

        if (!_.isEmpty(queries) && !_.isEmpty(queriesPagination)) {
          const { currentPage, totalPages } = queriesPagination;
          if(currentPage < totalPages){
            const wHeight = window.innerHeight;
            const {top, bottom, height} = this.$refs.table.$el.getBoundingClientRect();
            const margin = 100;
            if(top * -1 + wHeight >= height - margin && isRulesBusy == false){
              const {pagination} = this;
              this.pagination = {
                ...pagination,
                page: pagination.page + 1
              }
            }
          }
        }
      },
      onSubmitRulesToDelete (ruleIds) {
        const { projectId } = this;
        this.removeAssignmentRules({ projectId, ruleIds });
        this.selectedRows = [];
        this.showPanel = false;
      },

      getRelevancyNameOptions (rs_type) {
        this.slectedRelevancyName = this.relevancyList
          .filter((ele) => ele.rs_type === rs_type)
          .map((ele) => ({ label: ele.rs_name, value: ele.id }));
      },
    },
    data () {
      return {
        tags: [],
        excludeTags: [],
        privateTags: [],
        excludePrivateTags: [],
        postTypes: [],
        excludePostTypes: [],
        includeHandleOptions: [],
        excludeHandleOptions: [],
        slectedRelevancyName: [],
        pagination: {
          page: 1,
          perPage: 20,
          // order: 'date',
          // sort: 'desc'
        },
        showPanel: false,
        ruleModel: {
          tags: [],
          exclude_tags: [],
          private_tags: [],
          exclude_private_tags: [],
          post_types: [],
          exclude_post_types: [],
          value: ''
        },
        selectedRows: [],
        selectedOutlets: [],
        selectedOutletGroups: [],
        showSocialFilters: false
      };
    },
    mixins: [propsForMixin, runShortcut('addTag')],
  };
</script>


<style lang="scss" scoped>
  @import "@/styles/components/_datatable_bulk_actions.scss";
  @import "@/styles/components/_loader.scss";
</style>